import { useSyncExternalStore } from "react";
import { z } from "zod";

import type { JobSeekerProfileGeocodePrecision } from "../../__generated__/types.gqlgenerated";
import { localPrefs } from "../native-modules/tare/getLocalPrefs";

const RN_HOMEPAGE_NAMESPACE = "RnHomepage";
const USER_LOCATION_KEY = "UserLocation";

export type UserLocation = {
  latitude: number;
  longitude: number;
  geocodePrecision?: JobSeekerProfileGeocodePrecision | null;
  streetAddress?: string;
  formattedLocation?: string;
  city?: string;
  admin1?: string;
  admin2?: string;
  admin3?: string;
  admin4?: string;
  postalCode?: string;
  country?: string;
  maxCommuteMinutes?: number | null;
};

type Snapshot = {
  location: UserLocation | null;
  loading: boolean;
};

// Resilient rather than strict: the stored value was written by whatever app version
// last ran, so only the coordinates consumers require are enforced — a bad leaf or an
// unmodelled key must not cost the seeker their saved location.
const optionalString = z.string().optional().catch(undefined);

const userLocationSchema = z
  .object({
    latitude: z.number().finite(),
    longitude: z.number().finite(),
    // Left wide because "is a string" is all any reader relies on: `useHomepageInitData`
    // and `useLoadMoreJobFeedDataFromJRS` only compare it to `"ADDRESS"`; `CommuteSection`
    // and `CommutePreferenceModal` only round-trip it. None indexes, switches
    // exhaustively, or sends it as a mutation variable — so narrowing to the current enum
    // members would drop a precision a newer build wrote and erase it on write-back.
    geocodePrecision: z
      .custom<JobSeekerProfileGeocodePrecision>((value) => typeof value === "string")
      .nullish()
      .catch(undefined),
    streetAddress: optionalString,
    formattedLocation: optionalString,
    city: optionalString,
    admin1: optionalString,
    admin2: optionalString,
    admin3: optionalString,
    admin4: optionalString,
    postalCode: optionalString,
    country: optionalString,
    maxCommuteMinutes: z.number().finite().nullish().catch(undefined),
  } satisfies Record<keyof UserLocation, z.ZodTypeAny>)
  .passthrough();

function parseStoredLocation(locationStr: string): UserLocation | null {
  let raw: unknown;
  try {
    raw = JSON.parse(locationStr);
  } catch {
    console.error("Stored user location is not valid JSON; ignoring it");
    return null;
  }

  const result = userLocationSchema.safeParse(raw);
  if (result.success) {
    return result.data;
  }

  // Issue paths and codes only — a stored location holds the seeker's street address.
  console.error(
    "Stored user location failed validation; ignoring it:",
    result.error.issues
      .map((issue) => `${issue.path.join(".") || "(root)"}: ${issue.code}`)
      .join(", "),
  );
  return null;
}

// Module-level shared state so every consumer of the hook sees the same
// value and re-renders when any caller writes a new value. The snapshot
// reference is stable between mutations — required by useSyncExternalStore
// to avoid tearing and infinite re-renders.
let currentSnapshot: Snapshot = { location: null, loading: true };
let hasLoaded = false;
let loadPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

function setSnapshot(next: Snapshot) {
  currentSnapshot = next;
  for (const cb of listeners) cb();
}

function ensureLoaded(): Promise<void> {
  if (hasLoaded) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = localPrefs
    .getString(RN_HOMEPAGE_NAMESPACE, USER_LOCATION_KEY)
    .then((locationStr) => {
      const parsed = locationStr ? parseStoredLocation(locationStr) : null;
      hasLoaded = true;
      setSnapshot({ location: parsed, loading: false });
    })
    .catch((error) => {
      console.error("Failed to read stored user location:", error);
      hasLoaded = true;
      setSnapshot({ location: currentSnapshot.location, loading: false });
    });
  return loadPromise;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  void ensureLoaded();
  return () => {
    listeners.delete(cb);
  };
}

function getSnapshot(): Snapshot {
  return currentSnapshot;
}

// Required by useSyncExternalStore for SSR. Without it React aborts the
// server render of this subtree and reverts to client rendering, defeating SSR.
// Return a stable reference so React doesn't see it change between SSR passes.
const serverSnapshot: Snapshot = { location: null, loading: true };
function getServerSnapshot(): Snapshot {
  return serverSnapshot;
}

/**
 * Clears the stored user location from both localPrefs and the in-memory
 * cache, then notifies subscribers so any active `useStoredUserLocation`
 * consumer re-renders with `storedLocation: null`. Resets `hasLoaded` so
 * the next consumer mount re-reads localPrefs from scratch — useful when
 * external code (native modules, another window, logout flows) may have
 * mutated the underlying storage.
 */
export async function clearStoredUserLocation(): Promise<void> {
  await localPrefs
    .setString(RN_HOMEPAGE_NAMESPACE, USER_LOCATION_KEY, "")
    .catch((error) => console.error("Failed to clear stored user location:", error));
  hasLoaded = false;
  loadPromise = null;
  setSnapshot({ location: null, loading: false });
}

export async function updateStoredLocation(location: UserLocation): Promise<void> {
  await localPrefs
    .setString(RN_HOMEPAGE_NAMESPACE, USER_LOCATION_KEY, JSON.stringify(location))
    .catch((error) => console.error("Failed to store user location:", error));
  hasLoaded = true;
  setSnapshot({ location, loading: false });
}

export function useStoredUserLocation() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return {
    storedLocation: snapshot.location,
    loading: snapshot.loading,
    updateStoredLocation,
    clearStoredUserLocation,
  };
}
